import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { BaseIcon } from "@shltelemedicine/shl-ui-lib";
import userInfoIcon from "../../../assets/users/user_info.svg";
import adminInfoIcon from "../../../assets/users/admin_Info.svg";
import styles from "./SettingsInfoInputs.module.css";
import { Form, Spinner } from "react-bootstrap";
import TextFormInput from "../../Shared/TextInput/TextFormInput";
import ConnectForm from "../../Shared/ConnectForm/ConnectForm";
import { Button, FormWarning } from "@shltelemedicine/shl-ui-lib";
import { SettingsInfoFormInputs } from "../../../models/settings.interface";
import applyIcon from "../../../assets/buttons/approve.svg";
import { FormProvider, useForm } from "react-hook-form";
import useAlert from "../../../hooks/useAlert";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import {
  IAuthenticatedUser,
  IUpdateCurrentUserInfo,
} from "../../../models/authenticated-user.interface";
import { getFormChangedData } from "../../../utils/formUtil";
import usersService from "../../../services/users.service";
import TitleSelectFormInput from "../../Management/ManageProUsers/TitleSelectFormInput/TitleSelectFormInput";
import ConfirmationModal from "../../Shared/ConfirmationModal/ConfirmationModal";
import { AuthActionType, useAuthContext } from "../../../context/AuthContext";
import userInfoUpdatedIcon from "../../../assets/settings/user_info_updated.svg";
import adminInfoUpdatedIcon from "../../../assets/settings/admin_info_updated.svg";
import { isAdmin } from "../../../utils/userUtil";

type SettingsInfoInputsProps = {
  user: IAuthenticatedUser;
};

const SettingsInfoInputs = ({ user }: SettingsInfoInputsProps) => {
  const infoFormMethods = useForm<SettingsInfoFormInputs>();
  const [loading, setLoading] = useState(false);
  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();
  const [showInfoConfirmModal, setShowInfoConfirmModal] = useState(false);

  const { dispatch } = useAuthContext();

  const {
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields },
    getValues,
  } = infoFormMethods;

  useEffect(() => {
    if (!user) return;

    reset(mapToSettingsInfoForm(user));
  }, [reset, user]);

  const applyUserInfoHandler = async () => {
    setLoading(true);
    let titleChanged: IUpdateCurrentUserInfo | undefined;

    let changedData = getFormChangedData<SettingsInfoFormInputs>(
      dirtyFields,
      getValues
    );

    if ("title" in changedData) titleChanged = mapToUserInfo(changedData);

    try {
      const { success, errorCode } = await usersService.updateCurrentUserInfo(
        titleChanged ?? changedData
      );

      if (!success) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.UPDATE_USER",
          "An error occurred while updating user information.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }
      setShowInfoConfirmModal(true);

      dispatch({
        type: AuthActionType.SET_USER,
        payload: mapToUserUpdatedInfo(user, changedData),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...infoFormMethods}>
      <ConnectForm<SettingsInfoFormInputs>>
        {({ control, formState: { errors: formErrors } }) => {
          return (
            <div className="container">
              <Form.Row className="mb-3">
                <div className="d-flex align-items-center">
                  <BaseIcon
                    className=""
                    icon={isAdmin(user) ? adminInfoIcon : userInfoIcon}
                  />
                  <span className="mx-1"></span>
                  <p className={`${styles.title} mb-0`}>
                    <FormattedMessage
                      id="SETTINGS.MY_INFO"
                      defaultMessage="My Info"
                    />
                  </p>
                </div>
              </Form.Row>
              <Form.Row className="mb-2">
                <div
                  className={`${styles.label} d-lg-flex justify-content-lg-center col align-items-center flex-grow-1`}
                >
                  <div className="w-25 text-nowrap">
                    <Form.Label className=" mb-1 font-md align-items-center ">
                      <FormattedMessage
                        id="USER.FAMILY_NAME"
                        defaultMessage="Family Name"
                      />
                    </Form.Label>
                  </div>
                  <div className="w-100">
                    <TextFormInput<SettingsInfoFormInputs>
                      name="lastName"
                      input={{
                        disabled: loading,
                        isInvalid: !!formErrors?.lastName,
                      }}
                      control={control}
                      rules={{
                        required: true,
                      }}
                    />
                    {!!formErrors?.lastName && (
                      <FormWarning
                        className="font-sm flex-grow-1 mt-2"
                        message={
                          <FormattedMessage
                            id="SETTINGS.FAMILY_NAME_REQUIRED"
                            defaultMessage="Insert Family Name"
                          />
                        }
                      />
                    )}
                  </div>
                </div>
              </Form.Row>
              <Form.Row className="mb-2">
                <div
                  className={`${styles.label} d-lg-flex justify-content-lg-center col align-items-center flex-grow-1`}
                >
                  <div className="w-25 text-nowrap">
                    <Form.Label className="mb-1 font-md align-items-center">
                      <FormattedMessage id="USER.NAME" defaultMessage="Name" />
                    </Form.Label>
                  </div>
                  <div className="w-100">
                    <TextFormInput<SettingsInfoFormInputs>
                      name="firstName"
                      input={{
                        disabled: loading,
                        isInvalid: !!formErrors?.firstName,
                      }}
                      control={control}
                      rules={{
                        required: true,
                      }}
                    />
                    {!!formErrors?.firstName && (
                      <FormWarning
                        className="font-sm flex-grow-1 mt-2"
                        message={
                          <FormattedMessage
                            id="SETTINGS.NAME_REQUIRED"
                            defaultMessage="Insert Name"
                          />
                        }
                      />
                    )}
                  </div>
                </div>
              </Form.Row>
              <Form.Row className="mb-2">
                <div
                  className={`${styles.label} d-lg-flex justify-content-lg-center col align-items-center flex-grow-1`}
                >
                  <div className="w-25 text-nowrap">
                    <Form.Label className="mb-1 font-md align-items-center">
                      <FormattedMessage
                        id="USER.TITLE"
                        defaultMessage="Title"
                      />
                    </Form.Label>
                  </div>

                  <div className="w-100">
                    <TitleSelectFormInput
                      control={control}
                      disabled={loading}
                    />
                  </div>
                </div>
              </Form.Row>
              <Form.Row className="d-flex justify-content-end px-1">
                <Button
                  type="submit"
                  icon={{
                    src: applyIcon,
                    width: 40,
                    height: 22,
                  }}
                  bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
                  className="capitalized px-5"
                  disabled={!isDirty ?? true}
                  onClick={handleSubmit(applyUserInfoHandler)}
                >
                  <FormattedMessage
                    id="SETTINGS.APPLY_INFO"
                    defaultMessage="Apply"
                  />
                  {loading && (
                    <>
                      &nbsp;&nbsp;
                      <Spinner as="span" animation="border" size="sm" />
                    </>
                  )}
                </Button>
              </Form.Row>
            </div>
          );
        }}
      </ConnectForm>
      {showInfoConfirmModal && (
        <ConfirmationModal
          show={showInfoConfirmModal}
          okBtn={{
            onClick: () => {
              setShowInfoConfirmModal(false);
            },
          }}
          title={
            <FormattedMessage
              id="SETTINGS.INFO_UPDATED"
              defaultMessage="Info Updated"
            />
          }
          onClose={() => setShowInfoConfirmModal(false)}
          headerIcon={
            isAdmin(user) ? adminInfoUpdatedIcon : userInfoUpdatedIcon
          }
        >
          <FormattedMessage
            id="SETTINGS.INFO_UPDATED_SUCCESSFULLY"
            defaultMessage="Info Updated Successfully"
          />
        </ConfirmationModal>
      )}
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </FormProvider>
  );
};

export default SettingsInfoInputs;

const mapToSettingsInfoForm = (
  user: IAuthenticatedUser
): SettingsInfoFormInputs => {
  const { firstName, lastName, title } = user;

  return {
    lastName,
    firstName,
    title,
  };
};

const mapToUserInfo = (
  user: Partial<SettingsInfoFormInputs>
): IUpdateCurrentUserInfo => {
  const { firstName, lastName, title } = user;

  return {
    lastName,
    firstName,
    salutation: title,
  };
};
const mapToUserUpdatedInfo = (
  user: IAuthenticatedUser,
  changedInfo: Partial<SettingsInfoFormInputs>
): IAuthenticatedUser => {
  const {
    id,
    firstName,
    lastName,
    email,
    organization,
    role,
    permissions,
    title,
    isSso,
  } = user;

  return {
    id,
    firstName: changedInfo.firstName ?? firstName,
    lastName: changedInfo.lastName ?? lastName,
    email,
    organization,
    role,
    permissions,
    title: changedInfo.title ?? title,
    isSso,
  };
};
